/* istanbul ignore file */
/* tslint:disable */
 
import type { Broker } from '../models/Broker';
import type { BrokerChangeAdmin } from '../models/BrokerChangeAdmin';
import type { BrokerConfirmInvite } from '../models/BrokerConfirmInvite';
import type { BrokerDetail } from '../models/BrokerDetail';
import type { BrokerInvite } from '../models/BrokerInvite';
import type { BrokerInviteUser } from '../models/BrokerInviteUser';
import type { BrokerMediaCreate } from '../models/BrokerMediaCreate';
import type { BrokerMediaDetail } from '../models/BrokerMediaDetail';
import type { BrokerPermissions } from '../models/BrokerPermissions';
import type { BrokerPersonalApiSettings } from '../models/BrokerPersonalApiSettings';
import type { BrokerProductBulkUpdate } from '../models/BrokerProductBulkUpdate';
import type { BrokerProfile } from '../models/BrokerProfile';
import type { BrokerTeam } from '../models/BrokerTeam';
import type { BrokerTeamList } from '../models/BrokerTeamList';
import type { BrokerTeamUsers } from '../models/BrokerTeamUsers';
import type { BrokerTokenType } from '../models/BrokerTokenType';
import type { BrokerUser } from '../models/BrokerUser';
import type { BrokerUserDetail } from '../models/BrokerUserDetail';
import type { PaginatedBrokerListList } from '../models/PaginatedBrokerListList';
import type { PaginatedBrokerMediaList } from '../models/PaginatedBrokerMediaList';
import type { PaginatedBrokerProductAssignmentList } from '../models/PaginatedBrokerProductAssignmentList';
import type { PaginatedBrokerTeamListList } from '../models/PaginatedBrokerTeamListList';
import type { PaginatedBrokerUserDetailList } from '../models/PaginatedBrokerUserDetailList';
import type { PatchedBroker } from '../models/PatchedBroker';
import type { PatchedBrokerPersonalApiSettings } from '../models/PatchedBrokerPersonalApiSettings';
import type { PatchedBrokerProfile } from '../models/PatchedBrokerProfile';
import type { PatchedBrokerRotatedToken } from '../models/PatchedBrokerRotatedToken';
import type { PatchedBrokerTeam } from '../models/PatchedBrokerTeam';
import type { PatchedBrokerUser } from '../models/PatchedBrokerUser';
import type { UserStatus } from '../models/UserStatus';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class BrokersService {

  /**
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: created, modified, name, is_active, status, iban, address, idno, iban_commission
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, idno, admin__email
   * @param status * `draft` - Draft
 * * `pending_email` - E-mail în așteptare
 * * `active` - Activ
 * * `inactive` - Inactiv
   * @returns PaginatedBrokerListList 
   * @throws ApiError
   */
  public static brokersBrokersList(
excludeIds?: string,
ids?: string,
name?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
status?: 'active' | 'draft' | 'inactive' | 'pending_email',
): CancelablePromise<PaginatedBrokerListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/brokers/',
      query: {
        'exclude_ids': excludeIds,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'status': status,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns Broker 
   * @throws ApiError
   */
  public static brokersBrokersCreate(
requestBody: Broker,
): CancelablePromise<Broker> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/brokers/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param broker 
   * @param ordering Ordering fields: media__name, created
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: media__name
   * @returns PaginatedBrokerMediaList 
   * @throws ApiError
   */
  public static brokersBrokersMediaList(
broker?: number,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedBrokerMediaList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/brokers-media/',
      query: {
        'broker': broker,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param formData 
   * @returns BrokerMediaCreate 
   * @throws ApiError
   */
  public static brokersBrokersMediaCreate(
formData: BrokerMediaCreate,
): CancelablePromise<BrokerMediaCreate> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/brokers-media/',
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * @param id A unique integer value identifying this broker media.
   * @returns BrokerMediaDetail 
   * @throws ApiError
   */
  public static brokersBrokersMediaRetrieve(
id: number,
): CancelablePromise<BrokerMediaDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/brokers-media/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this broker media.
   * @returns void 
   * @throws ApiError
   */
  public static brokersBrokersMediaDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/brokers/brokers-media/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this broker.
   * @returns BrokerDetail 
   * @throws ApiError
   */
  public static brokersBrokersRetrieve(
id: number,
): CancelablePromise<BrokerDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/brokers/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this broker.
   * @param requestBody 
   * @returns Broker 
   * @throws ApiError
   */
  public static brokersBrokersPartialUpdate(
id: number,
requestBody?: PatchedBroker,
): CancelablePromise<Broker> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/brokers/brokers/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this broker.
   * @returns void 
   * @throws ApiError
   */
  public static brokersBrokersDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/brokers/brokers/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this broker.
   * @param requestBody 
   * @returns Broker 
   * @throws ApiError
   */
  public static brokersBrokersChangeAdminCreate(
id: number,
requestBody: BrokerChangeAdmin,
): CancelablePromise<Broker> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/brokers/{id}/change-admin/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this broker.
   * @param requestBody 
   * @returns Broker 
   * @throws ApiError
   */
  public static brokersBrokersInviteAdminCreate(
id: number,
requestBody: BrokerInvite,
): CancelablePromise<Broker> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/brokers/{id}/invite-admin/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Get Broker Products
   * Get all products assigned to a specific broker with their commission rates.
   * @param id A unique integer value identifying this broker.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: created, modified, name, is_active, status, iban, address, idno, iban_commission
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, idno, admin__email
   * @param status * `draft` - Draft
 * * `pending_email` - E-mail în așteptare
 * * `active` - Activ
 * * `inactive` - Inactiv
   * @returns PaginatedBrokerProductAssignmentList 
   * @throws ApiError
   */
  public static brokersBrokersProductsList(
id: number,
excludeIds?: string,
ids?: string,
name?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
status?: 'active' | 'draft' | 'inactive' | 'pending_email',
): CancelablePromise<PaginatedBrokerProductAssignmentList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/brokers/{id}/products/',
      path: {
        'id': id,
      },
      query: {
        'exclude_ids': excludeIds,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'status': status,
      },
    });
  }

  /**
   * Remove Broker Product
   * Remove a product assignment from a broker. This will delete the broker-product relationship.
   * @param id A unique integer value identifying this broker.
   * @param productId ID of the product to remove from the broker
   * @returns void 
   * @throws ApiError
   */
  public static brokersBrokersProductsDestroy(
id: number,
productId: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/brokers/brokers/{id}/products/{product_id}/',
      path: {
        'id': id,
        'product_id': productId,
      },
    });
  }

  /**
   * Bulk Update Broker Products
   * Set(upsert & delete) products assigned to a broker.
 *
 * Optionally configures the broker-level settings of each product via `config`, whose schema belongs to the product itself. Keys omitted from the request keep their stored values; every change is snapshotted into the by-date configuration history.
   * @param id A unique integer value identifying this broker.
   * @param requestBody 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: created, modified, name, is_active, status, iban, address, idno, iban_commission
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, idno, admin__email
   * @param status * `draft` - Draft
 * * `pending_email` - E-mail în așteptare
 * * `active` - Activ
 * * `inactive` - Inactiv
   * @returns PaginatedBrokerProductAssignmentList 
   * @throws ApiError
   */
  public static brokersBrokersProductsBulkUpdateCreate(
id: number,
requestBody: BrokerProductBulkUpdate,
excludeIds?: string,
ids?: string,
name?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
status?: 'active' | 'draft' | 'inactive' | 'pending_email',
): CancelablePromise<PaginatedBrokerProductAssignmentList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/brokers/{id}/products/bulk-update/',
      path: {
        'id': id,
      },
      query: {
        'exclude_ids': excludeIds,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'status': status,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * re invite existing inactive broker admin
   * @param id A unique integer value identifying this broker.
   * @returns Broker 
   * @throws ApiError
   */
  public static brokersBrokersReInviteAdminCreate(
id: number,
): CancelablePromise<Broker> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/brokers/{id}/re-invite-admin/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns BrokerUser 
   * @throws ApiError
   */
  public static brokersBrokersConfirmInviteCreate(
requestBody: BrokerConfirmInvite,
): CancelablePromise<BrokerUser> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/brokers/confirm-invite/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns BrokerProfile 
   * @throws ApiError
   */
  public static brokersBrokersProfileRetrieve(): CancelablePromise<BrokerProfile> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/brokers/profile/',
    });
  }

  /**
   * @param requestBody 
   * @returns BrokerProfile 
   * @throws ApiError
   */
  public static brokersBrokersProfilePartialUpdate(
requestBody?: PatchedBrokerProfile,
): CancelablePromise<BrokerProfile> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/brokers/brokers/profile/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns BrokerPersonalApiSettings 
   * @throws ApiError
   */
  public static brokersPersonalApiSettingsRetrieve(): CancelablePromise<BrokerPersonalApiSettings> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/personal/api-settings/',
    });
  }

  /**
   * @param requestBody 
   * @returns BrokerPersonalApiSettings 
   * @throws ApiError
   */
  public static brokersPersonalApiSettingsPartialUpdate(
requestBody?: PatchedBrokerPersonalApiSettings,
): CancelablePromise<BrokerPersonalApiSettings> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/brokers/personal/api-settings/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @returns PatchedBrokerRotatedToken 
   * @throws ApiError
   */
  public static brokersPersonalRotateTokenCreate(
requestBody: BrokerTokenType,
): CancelablePromise<PatchedBrokerRotatedToken> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/personal/rotate-token/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param broker 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: name, parent
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedBrokerTeamListList 
   * @throws ApiError
   */
  public static brokersTeamsList(
broker?: string,
excludeIds?: string,
ids?: string,
name?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedBrokerTeamListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/teams/',
      query: {
        'broker': broker,
        'exclude_ids': excludeIds,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns BrokerTeam 
   * @throws ApiError
   */
  public static brokersTeamsCreate(
requestBody: BrokerTeam,
): CancelablePromise<BrokerTeam> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/teams/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @returns BrokerTeamList 
   * @throws ApiError
   */
  public static brokersTeamsRetrieve(
id: string,
): CancelablePromise<BrokerTeamList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/teams/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id 
   * @param requestBody 
   * @returns BrokerTeam 
   * @throws ApiError
   */
  public static brokersTeamsPartialUpdate(
id: string,
requestBody?: PatchedBrokerTeam,
): CancelablePromise<BrokerTeam> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/brokers/teams/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @returns void 
   * @throws ApiError
   */
  public static brokersTeamsDestroy(
id: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/brokers/teams/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id 
   * @param requestBody 
   * @returns BrokerTeamList 
   * @throws ApiError
   */
  public static brokersTeamsAddUsersCreate(
id: string,
requestBody: BrokerTeamUsers,
): CancelablePromise<BrokerTeamList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/teams/{id}/add-users/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id 
   * @param requestBody 
   * @returns BrokerTeamList 
   * @throws ApiError
   */
  public static brokersTeamsRemoveUserCreate(
id: string,
requestBody: BrokerTeamUsers,
): CancelablePromise<BrokerTeamList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/teams/{id}/remove-user/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param broker 
   * @returns BrokerPermissions 
   * @throws ApiError
   */
  public static brokersTeamsAvailablePermissionsList(
broker: number,
): CancelablePromise<Array<BrokerPermissions>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/teams/available-permissions/',
      query: {
        'broker': broker,
      },
    });
  }

  /**
   * @param broker 
   * @param email 
   * @param excludeIds comma separated list
   * @param excludeStatus Exclude utilizatorii care au oricare dintre statusurile transmise.
 *
 * * `active` - Activ
 * * `pending` - E-mail în așteptare
 * * `disabled` - Disabled
 * * `deleted` - Deleted
   * @param identificationNumber 
   * @param ids comma separated list
   * @param isBroker Filtrează utilizatorii în funcție de apartenența la un broker.
   * @param isStaff Filtrează utilizatorii în funcție de existența accesului de staff.
   * @param ordering Ordering fields: id, email, first_name, last_name, identification_number, status, teams
   * @param origin * `admin` - Admin
 * * `register` - Register
 * * `google` - Google
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param permissions Filtrează utilizatorii după permisiunile echipei.
 *
 * * `section_orders` - Secțiunea Comenzi
 * * `section_customers` - Secțiunea Clienți
 * * `section_reports` - Secțiunea Rapoarte
 * * `section_registers` - Secțiunea registre
 * * `section_sales` - Secțiunea vânzări
 * * `section_financial` - Dashboard financiar
 * * `sales_panel` - Panou de vânzări
 * * `contracts_registry` - Borderoul contractelor
 * * `no_organization_limit` - Fără limită de organizații/brokeri
 * * `no_personal_limit` - Vizualizare contracte fără restricții
 * * `confirm_settlement` - Confirmare decontarea
 * * `confirm_refund` - Confirmare rambursarea
 * * `manual_ecec_creation` - Creare manuală E-CEC
 * * `manage_ecec_instances` - Manage ECEc Instances
 * * `confirm_bank_transfer` - Confirmarea transferului bancar
 * * `rca_documents` - Acces la documetele din RCA
 * * `financial_data` - Acces la date financiare
 * * `approve_order` - Aprobare comanda
 * * `change_contract` - Modificarea sau rezilierea contractului
 * * `fiscal_code_entity` - Gestionare organizații fără IDNO
 * * `rca_mark_as_deleted` - Anulare polițe RCA
 * * `online_orders` - Acces la comenzi online
 * * `inspection` - Acces pentru inspecție CASCO
 * * `section_settings` - Secțiunea Setări
 * * `section_external_api` - Secțiunea API Extern
 * * `vehicles_settings` - Configurarea setărilor vehiculelor
 * * `edit_insured_limit_config` - Edit own capital & max insured percent
 * * `section_despagubiri` - Secțiunea Despăgubiri
 * * `manage_reserve_types` - Gestionarea tipurilor de rezerve de daune
 * * `section_claim_case_rcai` - Secțiunea dosarelor de despăgubiri RCAI
 * * `claim_case_rcai_operator` - Operator dosare de despăgubiri RCAI
 * * `claim_case_rcai_supervisor` - Supervisor dosare de despăgubiri RCAI
 * * `section_claim_case_rcae` - Secțiunea dosarelor de despăgubiri RCAE
 * * `claim_case_rcae_operator` - Operator dosare de despăgubiri RCAE
 * * `claim_case_rcae_supervisor` - Supervisor dosare de despăgubiri RCAE
 * * `claim_anti_fraud` - Echipa antifraudă
 * * `claim_case_rcai_expert` - Expert în daune de asigurare RCAI
 * * `claim_case_confirm_payment` - Confirmarea plății despăgubirilor
 * * `director` - Director
 * * `product_rca` - RCA
 * * `product_green_card` - Carte Verde
 * * `product_medical_travel` - Asigurarea medicală
 * * `product_accidents` - Asigurarea de accidente
 * * `product_casco` - Casco
 * * `product_property` - Asigurarea bunurilor
 * * `product_cmr` - Asigurarea CMR
 * * `product_financial` - Asigurarea riscurilor financiare de călătorie
   * @param search Search fields: email, first_name, last_name, phone
   * @param ssoId 
   * @param status Filtrează utilizatorii după status.
 *
 * * `active` - Activ
 * * `pending` - E-mail în așteptare
 * * `disabled` - Disabled
 * * `deleted` - Deleted
   * @param teams 
   * @returns PaginatedBrokerUserDetailList 
   * @throws ApiError
   */
  public static brokersUsersList(
broker?: number,
email?: string,
excludeIds?: string,
excludeStatus?: Array<'active' | 'deleted' | 'disabled' | 'pending'>,
identificationNumber?: string,
ids?: string,
isBroker?: boolean,
isStaff?: boolean,
ordering?: string,
origin?: 'admin' | 'google' | 'register' | null,
page?: number,
pageSize?: number,
permissions?: Array<'approve_order' | 'change_contract' | 'claim_anti_fraud' | 'claim_case_confirm_payment' | 'claim_case_rcae_operator' | 'claim_case_rcae_supervisor' | 'claim_case_rcai_expert' | 'claim_case_rcai_operator' | 'claim_case_rcai_supervisor' | 'confirm_bank_transfer' | 'confirm_refund' | 'confirm_settlement' | 'contracts_registry' | 'director' | 'edit_insured_limit_config' | 'financial_data' | 'fiscal_code_entity' | 'inspection' | 'manage_ecec_instances' | 'manage_reserve_types' | 'manual_ecec_creation' | 'no_organization_limit' | 'no_personal_limit' | 'online_orders' | 'product_accidents' | 'product_casco' | 'product_cmr' | 'product_financial' | 'product_green_card' | 'product_medical_travel' | 'product_property' | 'product_rca' | 'rca_documents' | 'rca_mark_as_deleted' | 'sales_panel' | 'section_claim_case_rcae' | 'section_claim_case_rcai' | 'section_customers' | 'section_despagubiri' | 'section_external_api' | 'section_financial' | 'section_orders' | 'section_registers' | 'section_reports' | 'section_sales' | 'section_settings' | 'vehicles_settings'>,
search?: string,
ssoId?: string,
status?: Array<'active' | 'deleted' | 'disabled' | 'pending'>,
teams?: Array<number>,
): CancelablePromise<PaginatedBrokerUserDetailList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/users/',
      query: {
        'broker': broker,
        'email': email,
        'exclude_ids': excludeIds,
        'exclude_status': excludeStatus,
        'identification_number': identificationNumber,
        'ids': ids,
        'is_broker': isBroker,
        'is_staff': isStaff,
        'ordering': ordering,
        'origin': origin,
        'page': page,
        'page_size': pageSize,
        'permissions': permissions,
        'search': search,
        'sso_id': ssoId,
        'status': status,
        'teams': teams,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this utilizator.
   * @returns BrokerUserDetail 
   * @throws ApiError
   */
  public static brokersUsersRetrieve(
id: number,
): CancelablePromise<BrokerUserDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/users/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this utilizator.
   * @param requestBody 
   * @returns BrokerUser 
   * @throws ApiError
   */
  public static brokersUsersPartialUpdate(
id: number,
requestBody?: PatchedBrokerUser,
): CancelablePromise<BrokerUser> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/brokers/users/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this utilizator.
   * @returns void 
   * @throws ApiError
   */
  public static brokersUsersDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/brokers/users/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this utilizator.
   * @returns any 
   * @throws ApiError
   */
  public static brokersUsersResetPasswordCreate(
id: number,
): CancelablePromise<Record<string, any>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/users/{id}/reset-password/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this utilizator.
   * @param requestBody 
   * @returns BrokerUser 
   * @throws ApiError
   */
  public static brokersUsersSetStatusCreate(
id: number,
requestBody: UserStatus,
): CancelablePromise<BrokerUser> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/users/{id}/set-status/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @returns BrokerUser 
   * @throws ApiError
   */
  public static brokersUsersInviteCreate(
requestBody: BrokerInviteUser,
): CancelablePromise<BrokerUser> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/brokers/users/invite/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param broker 
   * @param email 
   * @param excludeIds comma separated list
   * @param excludeStatus Exclude utilizatorii care au oricare dintre statusurile transmise.
 *
 * * `active` - Activ
 * * `pending` - E-mail în așteptare
 * * `disabled` - Disabled
 * * `deleted` - Deleted
   * @param identificationNumber 
   * @param ids comma separated list
   * @param isBroker Filtrează utilizatorii în funcție de apartenența la un broker.
   * @param isStaff Filtrează utilizatorii în funcție de existența accesului de staff.
   * @param ordering Ordering fields: id, email, first_name, last_name, identification_number, status, teams
   * @param origin * `admin` - Admin
 * * `register` - Register
 * * `google` - Google
   * @param permissions Filtrează utilizatorii după permisiunile echipei.
 *
 * * `section_orders` - Secțiunea Comenzi
 * * `section_customers` - Secțiunea Clienți
 * * `section_reports` - Secțiunea Rapoarte
 * * `section_registers` - Secțiunea registre
 * * `section_sales` - Secțiunea vânzări
 * * `section_financial` - Dashboard financiar
 * * `sales_panel` - Panou de vânzări
 * * `contracts_registry` - Borderoul contractelor
 * * `no_organization_limit` - Fără limită de organizații/brokeri
 * * `no_personal_limit` - Vizualizare contracte fără restricții
 * * `confirm_settlement` - Confirmare decontarea
 * * `confirm_refund` - Confirmare rambursarea
 * * `manual_ecec_creation` - Creare manuală E-CEC
 * * `manage_ecec_instances` - Manage ECEc Instances
 * * `confirm_bank_transfer` - Confirmarea transferului bancar
 * * `rca_documents` - Acces la documetele din RCA
 * * `financial_data` - Acces la date financiare
 * * `approve_order` - Aprobare comanda
 * * `change_contract` - Modificarea sau rezilierea contractului
 * * `fiscal_code_entity` - Gestionare organizații fără IDNO
 * * `rca_mark_as_deleted` - Anulare polițe RCA
 * * `online_orders` - Acces la comenzi online
 * * `inspection` - Acces pentru inspecție CASCO
 * * `section_settings` - Secțiunea Setări
 * * `section_external_api` - Secțiunea API Extern
 * * `vehicles_settings` - Configurarea setărilor vehiculelor
 * * `edit_insured_limit_config` - Edit own capital & max insured percent
 * * `section_despagubiri` - Secțiunea Despăgubiri
 * * `manage_reserve_types` - Gestionarea tipurilor de rezerve de daune
 * * `section_claim_case_rcai` - Secțiunea dosarelor de despăgubiri RCAI
 * * `claim_case_rcai_operator` - Operator dosare de despăgubiri RCAI
 * * `claim_case_rcai_supervisor` - Supervisor dosare de despăgubiri RCAI
 * * `section_claim_case_rcae` - Secțiunea dosarelor de despăgubiri RCAE
 * * `claim_case_rcae_operator` - Operator dosare de despăgubiri RCAE
 * * `claim_case_rcae_supervisor` - Supervisor dosare de despăgubiri RCAE
 * * `claim_anti_fraud` - Echipa antifraudă
 * * `claim_case_rcai_expert` - Expert în daune de asigurare RCAI
 * * `claim_case_confirm_payment` - Confirmarea plății despăgubirilor
 * * `director` - Director
 * * `product_rca` - RCA
 * * `product_green_card` - Carte Verde
 * * `product_medical_travel` - Asigurarea medicală
 * * `product_accidents` - Asigurarea de accidente
 * * `product_casco` - Casco
 * * `product_property` - Asigurarea bunurilor
 * * `product_cmr` - Asigurarea CMR
 * * `product_financial` - Asigurarea riscurilor financiare de călătorie
   * @param search Search fields: email, first_name, last_name, phone
   * @param ssoId 
   * @param status Filtrează utilizatorii după status.
 *
 * * `active` - Activ
 * * `pending` - E-mail în așteptare
 * * `disabled` - Disabled
 * * `deleted` - Deleted
   * @param teams 
   * @returns binary 
   * @throws ApiError
   */
  public static brokersUsersXlsxRetrieve(
broker?: number,
email?: string,
excludeIds?: string,
excludeStatus?: Array<'active' | 'deleted' | 'disabled' | 'pending'>,
identificationNumber?: string,
ids?: string,
isBroker?: boolean,
isStaff?: boolean,
ordering?: string,
origin?: 'admin' | 'google' | 'register' | null,
permissions?: Array<'approve_order' | 'change_contract' | 'claim_anti_fraud' | 'claim_case_confirm_payment' | 'claim_case_rcae_operator' | 'claim_case_rcae_supervisor' | 'claim_case_rcai_expert' | 'claim_case_rcai_operator' | 'claim_case_rcai_supervisor' | 'confirm_bank_transfer' | 'confirm_refund' | 'confirm_settlement' | 'contracts_registry' | 'director' | 'edit_insured_limit_config' | 'financial_data' | 'fiscal_code_entity' | 'inspection' | 'manage_ecec_instances' | 'manage_reserve_types' | 'manual_ecec_creation' | 'no_organization_limit' | 'no_personal_limit' | 'online_orders' | 'product_accidents' | 'product_casco' | 'product_cmr' | 'product_financial' | 'product_green_card' | 'product_medical_travel' | 'product_property' | 'product_rca' | 'rca_documents' | 'rca_mark_as_deleted' | 'sales_panel' | 'section_claim_case_rcae' | 'section_claim_case_rcai' | 'section_customers' | 'section_despagubiri' | 'section_external_api' | 'section_financial' | 'section_orders' | 'section_registers' | 'section_reports' | 'section_sales' | 'section_settings' | 'vehicles_settings'>,
search?: string,
ssoId?: string,
status?: Array<'active' | 'deleted' | 'disabled' | 'pending'>,
teams?: Array<number>,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/brokers/users/xlsx/',
      query: {
        'broker': broker,
        'email': email,
        'exclude_ids': excludeIds,
        'exclude_status': excludeStatus,
        'identification_number': identificationNumber,
        'ids': ids,
        'is_broker': isBroker,
        'is_staff': isStaff,
        'ordering': ordering,
        'origin': origin,
        'permissions': permissions,
        'search': search,
        'sso_id': ssoId,
        'status': status,
        'teams': teams,
      },
    });
  }

}
